import {
  Box3,
  Mesh,
  MeshPhongMaterial,
  Object3D,
  SphereGeometry,
  Vector3
} from 'three';
import { DragControls } from 'three/examples/jsm/controls/DragControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { LoadType, PartSubType } from '../../models/enums';
import { LoadsGroupItem } from '../../models/loadcase.model';
import { ArrowHelper } from '../addons/load-helpers/arrowhelper';
import {
  LOAD_INDICATOR_DEFAULT_LENGTH,
  LOAD_SPHERE_HEIGHT_SEGMENTS,
  LOAD_SPHERE_RADIUS,
  LOAD_SPHERE_WIDTH_SEGMENTS
} from '../engine.constants';
import {
  calculateCenter,
  calculateCenterOfMass,
  calculateVolume,
  getLoadIndicatorScale
} from '../helpers';
import { EngineContext } from './engine-context';

/**
 * Manages load case visualization: reaction forces, load case highlighting,
 * group edit stacks with force/moment arrows.
 * Extracted from Engine to reduce class size.
 */
export class LoadCaseVisualizationManager {
  constructor(private ctx: EngineContext) {}

  private resizeLoadIndicators(): void {
    const scale = getLoadIndicatorScale(this.ctx.objectInfo.size);
    const sphereScale = scale / LOAD_INDICATOR_DEFAULT_LENGTH;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.ctx.session.engine?.selector.objects.forEach((obj: any) => {
      const partArrow = obj.group.userData['part_arrow'] as ArrowHelper | undefined;
      const partMoment = obj.group.userData['part_moment'] as ArrowHelper | undefined;
      const partSphere = obj.group.userData['part_sphere'] as Mesh | undefined;
      partArrow?.setLength(scale);
      partMoment?.setLength(scale);
      partSphere?.scale.setScalar(sphereScale);
    });
  }

  toggleReactionForces(value?: boolean): void {
    // Hide first (both label and arrow)
    this.hideAllReactionForceVisuals();

    if (value) {
      (this.ctx as unknown as { reactionForces: boolean }).reactionForces = value;
    } else {
      (this.ctx as unknown as { reactionForces: boolean }).reactionForces =
        !(this.ctx as unknown as { reactionForces: boolean }).reactionForces;
    }

    this.refreshReactionForceVisuals();
  }

  /**
   * Re-render reaction-force labels and arrows using the *current* unit
   * system. No-op when the toggle is OFF. Call this from an Angular `effect`
   * watching `unitConversionService.unitSystem` so the labels live-update
   * when the user flips metric ↔ imperial in Project Settings.
   */
  refreshReactionForceVisuals(): void {
    // Bail when the toggle is OFF — leaves visuals hidden as-is.
    const showRequested = (this.ctx as unknown as { reactionForces: boolean }).reactionForces;
    if (!showRequested) return;

    // Hide first so a re-render starts from a clean state (avoids stale
    // arrows/labels for parts no longer in the current loadcase set).
    this.hideAllReactionForceVisuals();

    try {
      if (this.ctx.session.reactionForcesAvailable && this.ctx.session.reactionForces) {
        const loadCaseIndex = this.ctx.session.project.loadcases.findIndex(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (lc: any) => lc.id === this.ctx.session.engine?.currentLoadCase
        );
        const reactionForce = (this.ctx.session.reactionForces as unknown[])[loadCaseIndex] as
          | { id: string; x: number; y: number; z: number }[]
          | undefined;

        // Scale arrow length proportional to magnitude. baseLen is the
        // applied-load arrow length; reaction-force arrows are intentionally
        // smaller (max ~70% of an applied-load arrow) so they don't dominate
        // the scene when many constraints react. The min keeps tiny reactions
        // pickable.
        const baseLen = getLoadIndicatorScale(this.ctx.objectInfo.size);
        const minScale = 0.2;
        const maxScale = 0.7;
        // Find max magnitude across all reactions in this loadcase to normalise.
        const maxMag = (reactionForce ?? []).reduce((acc, p) => {
          const m = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
          return m > acc ? m : acc;
        }, 0);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.ctx.session.engine?.objects.forEach((obj: any) => {
          const control = obj.group.userData.part_reaction_force_annotation;
          const arrow = obj.group.userData.part_reaction_force_arrow as ArrowHelper | undefined;
          const match = reactionForce?.find((p) => p.id === obj.data.id);
          if (!match) return;

          const magnitude = Math.sqrt(match.x * match.x + match.y * match.y + match.z * match.z);

          // Convert to user's unit system (Imperial → lbf)
          const sys = this.ctx.unitConversionService.unitSystem();
          const xConv = this.ctx.unitConversionService.convert(match.x, 'force', sys);
          const yConv = this.ctx.unitConversionService.convert(match.y, 'force', sys);
          const zConv = this.ctx.unitConversionService.convert(match.z, 'force', sys);
          const magConv = this.ctx.unitConversionService.convert(magnitude, 'force', sys);
          const unit = this.ctx.unitConversionService.getUnitLabel('force', sys);

          control.setAnnotationText(
            this.formatForceVal(xConv),
            this.formatForceVal(yConv),
            this.formatForceVal(zConv),
            this.formatForceVal(magConv),
            unit,
          );
          control.annotation.visible = true;

          // Update the 3D arrow: direction (normalized) + length (scaled by
          // magnitude relative to the loadcase max).
          if (arrow && magnitude > 0) {
            const direction = new Vector3(match.x, match.y, match.z).normalize();
            arrow.setDirection(direction);
            const ratio = maxMag > 0 ? magnitude / maxMag : 1;
            const scaledLen = baseLen * (minScale + (maxScale - minScale) * ratio);
            arrow.setLength(scaledLen);
            arrow.visible = true;
          } else if (arrow) {
            // Zero-magnitude reaction (constraint with no load reacting through
            // it) — show the label but no arrow (would be degenerate).
            arrow.visible = false;
          }
        });
      }
    } catch (_) {}
  }

  hideReactionForces(): void {
    this.hideAllReactionForceVisuals();
    (this.ctx as unknown as { reactionForces: boolean }).reactionForces = false;
  }

  private hideAllReactionForceVisuals(): void {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.ctx.session.engine?.objects.forEach((obj: any) => {
        const control = obj.group.userData.part_reaction_force_annotation;
        if (control?.annotation) control.annotation.visible = false;
        const arrow = obj.group.userData.part_reaction_force_arrow as ArrowHelper | undefined;
        if (arrow) arrow.visible = false;
      });
    } catch (_) {}
  }

  private formatForceVal(value: number): string {
    if (!Number.isFinite(value)) return '0';
    // Compact display: integers when |v| ≥ 1, fixed-2 otherwise. Keeps the
    // label tight while preserving precision for sub-newton values.
    if (Math.abs(value) >= 100) return value.toFixed(0);
    if (Math.abs(value) >= 1) return value.toFixed(1);
    return value.toFixed(3);
  }

  toggleLoadCaseHighlight(value?: boolean): void {
    if (this.ctx.session.engine) {
      if (value) {
        this.ctx.session.engine.highlightLoadCase = value;
      } else {
        this.ctx.session.engine.highlightLoadCase = !this.ctx.session.engine?.highlightLoadCase;
      }

      if (this.ctx.session.engine?.highlightLoadCase) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loadcase = this.ctx.session.project.loadcases.find((o: any) => o.id === this.ctx.session.engine?.currentLoadCase);
        if (loadcase) {
          this.resizeLoadIndicators();
          for (const bc of loadcase.boundaryConditions) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const obj = this.ctx.session.engine?.selector.objects.find((o: any) => bc.partId === o.data.id);
            this.ctx.session.engine.selector.showBoundaryConditionAnnotation(obj.group);
            this.ctx.session.engine.selector.setPartColorBySubType(obj, PartSubType.BOUNDARY_CONDITION);
          }
          for (const load of loadcase.loads) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const obj = this.ctx.session.engine?.selector.objects.find((o: any) => load.partId === o.data.id);
            this.ctx.session.engine.selector.showLoadAnnotation(obj.group);
            this.ctx.session.engine.selector.showForceAndMomentWithManipulator(obj.group);
            this.ctx.session.engine.selector.setForceAndMomentWithManipulator(obj.group, load);
            this.ctx.session.engine.selector.dragForceAndMomentWithManipulator(obj.group, false);
            this.ctx.session.engine.selector.setPartColorBySubType(obj, PartSubType.LOAD);
          }
          for (const group of loadcase.groups) {
            for (const part of group.parts) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const obj = this.ctx.session.engine?.selector.objects.find((o: any) => part.id === o.data.id);
              this.ctx.session.engine?.selector.setPartColorBySubType(obj, PartSubType.LOAD);
              this.ctx.session.engine?.selector.hideForceAndMomentWithManipulator(obj.group);
            }
            this.createGroupEditStack(group);
            this.positionGroupEditStack(group);
            this.setForceAndMomentWithManipulator(true, group);
          }
        }
      } else {
        this.ctx.session.engine.selector.disableFaceSelection();
        this.ctx.session.engine.selector.restorePartsColorsToDesignAndNonDesign();
        for (const obj of this.ctx.session.engine.selector.objects) {
          this.ctx.session.engine.selector.hideBoundaryConditionAnnotation(obj.group);
          this.ctx.session.engine.selector.hideLoadAnnotation(obj.group);
          this.ctx.session.engine.selector.hideForceAndMomentWithManipulator(obj.group);
        }
        for (const loadcase of this.ctx.session.project.loadcases) {
          if (loadcase.groups?.length) {
            for (const group of loadcase.groups) {
              this.disposeGroupEditStack(group);
            }
          }
        }
      }
    }
  }

  positionGroupEditStack(group: LoadsGroupItem, force?: boolean): void {
    if (
      (Number.isNaN(group.remotePoint[0]) &&
        Number.isNaN(group.remotePoint[1]) &&
        Number.isNaN(group.remotePoint[2])) ||
      force
    ) {
      const objs = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const _objs = this.ctx.session.engine?.selector.objects.filter((o: any) =>
        group.parts.find((p) => p.id === o.data.id)
      );
      for (const obj of _objs) {
        const volume = calculateVolume(obj.data.faces);
        const center = calculateCenter(obj.group);
        objs.push({ center, volume });
      }

      const com = calculateCenterOfMass(objs);

      group.remotePoint[0] = com.x;
      group.remotePoint[1] = com.y;
      group.remotePoint[2] = com.z;
    }

    const vec = new Vector3(group.remotePoint[0], group.remotePoint[1], group.remotePoint[2]);
    group.groupEditStack?.controlMass.sphere!.position.copy(vec);
    group.groupEditStack?.force!.position.copy(vec);
    group.groupEditStack?.moment!.position.copy(vec);

    this.generateGroupEditStack(group);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createGroupEditStack(group: LoadsGroupItem): any {
    const arrowLength = getLoadIndicatorScale(this.ctx.objectInfo.size);
    const sphereRadius = arrowLength * (LOAD_SPHERE_RADIUS / LOAD_INDICATOR_DEFAULT_LENGTH);

    const CreateArrow = (name: string, type: 'line' | 'circle', dir: Vector3, color: number, center: Vector3): ArrowHelper => {
      const start = new Vector3(0, 0, arrowLength);
      const end = new Vector3(0, 0, 0);
      const direction = start.clone().sub(end);
      const length = direction.length();
      const obj = new ArrowHelper(type, direction.normalize(), end, length, color);
      obj.setDirection(dir);
      obj.name = name;
      obj.position.copy(center);
      obj.layers.set(0);
      return obj;
    };

    const CreateDragControl = (
      name: string,
      color: number,
      center: Vector3,
      _enabled: boolean = true
    ): { drag: DragControls; sphere: Mesh } => {
      const geometry = new SphereGeometry(sphereRadius, LOAD_SPHERE_WIDTH_SEGMENTS, LOAD_SPHERE_HEIGHT_SEGMENTS);
      const material = new MeshPhongMaterial({
        color: 0x000000,
        emissive: color,
        depthTest: false,
        alphaTest: 0,
        transparent: true,
        blendAlpha: 0
      });
      const sphere = new Mesh(geometry, material);
      sphere.name = name;
      sphere.position.copy(center);
      sphere.layers.set(0);

      const controls = new DragControls([sphere], this.ctx.camera, this.ctx.canvas);
      controls.enabled = false;

      sphere.userData['dragControl'] = controls;

      return { drag: controls, sphere };
    };

    const arrowForce = CreateArrow('part_arrow', 'line', new Vector3(0, 0, 0), 0x0000ff, new Vector3());
    const arrowMoment = CreateArrow('part_moment', 'circle', new Vector3(0, 0, 0), 0xff0000, new Vector3());
    CreateDragControl('groupEditControl', 0x000000, new Vector3(), false);
    const dragControlMass = CreateDragControl('groupEditControlMass', 0x779ecb, new Vector3(), true);

    const CreateDragControlMassAnnotation = (name: string, parent: Object3D, center: Vector3): CSS2DObject => {
      const div = document.createElement('div');
      div.className = 'annotation-box-text';
      const content = document.createElement('div');
      content.className = 'annotation-content';
      content.textContent = 'Mass';
      div.appendChild(content);

      const annotation = new CSS2DObject(div);
      annotation.name = name;
      annotation.position.copy(center);
      annotation.visible = false;
      parent.add(annotation);

      return annotation;
    };
    const dragControlMassBox = new Box3().setFromObject(dragControlMass.sphere);
    const dragControlMassCenter = dragControlMassBox.getCenter(new Vector3());
    dragControlMassCenter.z -= dragControlMassBox.getSize(new Vector3()).z * 1.5;
    const dragControlMassAnnotation = CreateDragControlMassAnnotation(
      'dragControlMassAnnotation',
      dragControlMass.sphere,
      dragControlMassCenter
    );

    this.ctx.scene.add(arrowForce);
    this.ctx.scene.add(arrowMoment);
    this.ctx.scene.add(dragControlMass.sphere);

    group.groupEditStack = {
      force: arrowForce,
      moment: arrowMoment,
      controlMass: {
        sphere: dragControlMass.sphere,
        drag: dragControlMass.drag,
        annotation: dragControlMassAnnotation
      },
      resources: {
        lines: [],
        materials: []
      }
    };

    return group.groupEditStack;
  }

  disposeGroupEditStack(group: LoadsGroupItem): void {
    if (group.groupEditStack?.resources.lines?.length) {
      for (const line of group.groupEditStack.resources.lines) {
        this.ctx.scene.remove(line);
        line.geometry.dispose();
      }
    }

    if (group.groupEditStack?.resources.materials?.length) {
      for (const mat of group.groupEditStack.resources.materials) {
        mat.dispose();
      }
    }

    if (group.groupEditStack?.force) this.ctx.scene.remove(group.groupEditStack.force);
    if (group.groupEditStack?.moment) this.ctx.scene.remove(group.groupEditStack.moment);
    if (group.groupEditStack?.controlMass?.sphere)
      this.ctx.scene.remove(group.groupEditStack.controlMass.sphere);

    if (group.groupEditStack?.controlMass?.drag) group.groupEditStack.controlMass.drag.dispose();
    if (group.groupEditStack?.controlMass?.annotation) group.groupEditStack.controlMass.annotation.visible = false;
    if (group.groupEditStack?.controlMass?.annotation) group.groupEditStack.controlMass.annotation.removeFromParent();

    if (group.groupEditStack) {
      group.groupEditStack.controlMass.annotation = undefined;
      group.groupEditStack.controlMass.drag = undefined;
      group.groupEditStack.controlMass.sphere = undefined;
      group.groupEditStack.force = undefined;
      group.groupEditStack.moment = undefined;
      group.groupEditStack.resources.lines = undefined;
      group.groupEditStack.resources.materials = undefined;
    }
  }

  generateGroupEditStack(group: LoadsGroupItem): void {
    const CreateLinkLine = (to: Vector3, _from?: Vector3, color: number = 0x000000): void => {
      const geometry = new LineGeometry();
      geometry.setPositions([
        group.groupEditStack!.controlMass.sphere!.position.x,
        group.groupEditStack!.controlMass.sphere!.position.y,
        group.groupEditStack!.controlMass.sphere!.position.z,
        to.x,
        to.y,
        to.z
      ]);

      const matLine = new LineMaterial({ color: color, linewidth: 3, dashed: true });
      const line = new Line2(geometry, matLine);
      line.computeLineDistances();
      line.scale.set(1, 1, 1);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      matLine.resolution.set((this.ctx as any).canvasWidth, (this.ctx as any).canvasHeight);

      group.groupEditStack!.resources.lines!.push(line);
      group.groupEditStack!.resources.materials!.push(matLine);

      this.ctx.scene.add(line);
    };

    for (const op of group.parts) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const obj = this.ctx.session.engine?.selector.objects.find((o: any) => op.id === o.data.id);
      CreateLinkLine(calculateCenter(obj.group));
    }
  }

  setForceAndMomentWithManipulator(updateControl: boolean = false, group: LoadsGroupItem): void {
    const remotePoint = new Vector3(group.remotePoint[0], group.remotePoint[1], group.remotePoint[2]);
    const forceComponent = new Vector3(group.forceComponent[0], group.forceComponent[1], group.forceComponent[2]);
    const momentComponent = new Vector3(group.momentComponent[0], group.momentComponent[1], group.momentComponent[2]);

    if (updateControl || group.type === LoadType.FORCE) group.groupEditStack?.moment!.position.copy(remotePoint);
    if (updateControl || group.type === LoadType.FORCE) group.groupEditStack?.force!.position.copy(remotePoint);

    group.groupEditStack?.controlMass.sphere!.position.copy(remotePoint);

    group.groupEditStack?.moment!.setDirection(momentComponent);
    group.groupEditStack?.force!.setDirection(forceComponent);

    if (forceComponent.x === 0 && forceComponent.y === 0 && forceComponent.z === 0) {
      group.groupEditStack?.force!.children.forEach((o) => o.layers.set(1));
    } else {
      group.groupEditStack?.force!.children.forEach((o) => o.layers.set(0));
    }

    if (momentComponent.x === 0 && momentComponent.y === 0 && momentComponent.z === 0) {
      group.groupEditStack?.moment!.children.forEach((o) => o.layers.set(1));
    } else {
      group.groupEditStack?.moment!.children.forEach((o) => o.layers.set(0));
    }
  }
}
